// All Firestore access for the `users` collection (Standards.md §2.1: no
// component or context talks to Firestore directly).
//
// Functions return plain data and throw on failure — state management and
// error UX stay with the callers.

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { COLLECTIONS } from 'constants/collections';
import { USER_ROLES } from 'constants/enums';

export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, COLLECTIONS.USERS, uid));
  return snap.exists() ? snap.data() : null;
};

export const createUserProfile = async (profile) => {
  await setDoc(doc(db, COLLECTIONS.USERS, profile.uid), profile);
};

// Merge-write arbitrary profile fields.
export const updateUserProfile = async (uid, data) => {
  await setDoc(doc(db, COLLECTIONS.USERS, uid), data, { merge: true });
};

// One-time bootstrap: promote a legacy admin email to role:'admin' in Firestore
// so Storage/Firestore rules can rely on the role field rather than hardcoded emails.
export const promoteToAdmin = async (uid) => {
  await setDoc(doc(db, COLLECTIONS.USERS, uid), { role: USER_ROLES.ADMIN }, { merge: true });
};

// Add/remove an event from the user's saved list.
export const setEventSaved = async (uid, eventId, save) => {
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
    savedEvents: save ? arrayUnion(eventId) : arrayRemove(eventId),
  });
};

// Register a newly created gym on the owner's profile.
export const addGymToProfile = async (uid, gymId) => {
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), { gymIds: arrayUnion(gymId) });
};

// A string is safe to use as a Firestore document path segment if it is non-empty
// and contains no forward slashes. More exotic characters are allowed by Firestore
// but a slash would split the path and cause an SDK error before the network call.
const isSafeDocPath = (s) => typeof s === 'string' && s.length > 0 && !s.includes('/');

// Public coach lookup for /profile/:id pages. Tries a direct doc get first,
// then falls back to querying the uid field (legacy docs whose id differs).
// Returns { profile, ownerId } or null — only for role === 'coach'.
export const getPublicCoachProfile = async (idOrUid) => {
  if (isSafeDocPath(idOrUid)) {
    const directSnap = await getDoc(doc(db, COLLECTIONS.USERS, idOrUid));
    if (directSnap.exists() && directSnap.data().role === USER_ROLES.COACH) {
      return { profile: directSnap.data(), ownerId: directSnap.id };
    }
  }
  const snap = await getDocs(
    query(collection(db, COLLECTIONS.USERS), where('uid', '==', idOrUid), limit(1))
  );
  if (!snap.empty && snap.docs[0].data().role === USER_ROLES.COACH) {
    return { profile: snap.docs[0].data(), ownerId: snap.docs[0].id };
  }
  return null;
};

// Purge a deleted account's Firestore footprint: owned events, owned gyms,
// then the user doc itself. Called AFTER the Auth account is deleted (the
// re-auth token must be spent on deleteUser first).
export const deleteAllUserData = async (uid) => {
  const eventsSnap = await getDocs(
    query(collection(db, COLLECTIONS.EVENTS), where('gymOwnerId', '==', uid))
  );
  await Promise.all(eventsSnap.docs.map((d) => deleteDoc(d.ref)));
  const gymsSnap = await getDocs(
    query(collection(db, COLLECTIONS.GYMS), where('ownerUserIds', 'array-contains', uid))
  );
  await Promise.all(gymsSnap.docs.map((d) => deleteDoc(d.ref)));
  await deleteDoc(doc(db, COLLECTIONS.USERS, uid));
};
